import _ from 'lodash';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { AbstractLimitationModel } from 'components/CampaignLimitation/CampaingLimitationForm/AbstractLimitation';
import { createSelectOptions } from 'utils/SelectOptionsUtils';

export type SearchKeywordsComponentState = {
  readonly loading: boolean;
  readonly relatedSearchKeywords: {
    keyword: string;
    searchCount: number;
  }[];
  readonly searchKeywords?: string;
};

export interface SearchKeywordsComponentModel extends AbstractLimitationModel {
  readonly state: SearchKeywordsComponentState;
  readonly event: UpdateEventListener<SearchKeywordsComponentModel>;
  readonly name: string;
  readonly value: SelectOptions[];
  onConfirm(): void;
  changeSearchKeyword(searchKeywords?: string): void;
  addSearchKeyword(searchKeyword: string): void;
  syncModel(model: SearchKeywordsComponentModel): void;
}

export type SearchKeywordsComponentProps = {
  readonly model: SearchKeywordsComponentModel;
};

export class DefaultSearchKeywordsComponentModel implements SearchKeywordsComponentModel {
  event: FireableUpdateEventListener<SearchKeywordsComponentModel>;
  loading = false;
  searchKeywords?: string;
  relatedSearchKeywords: {
    keyword: string;
    searchCount: number;
  }[] = [];

  constructor(
    public name: string,
    public value: SelectOptions[],
    private externalOnChange: (type: string, value: SelectOptions[]) => void
  ) {
    this.event =
      new FireableUpdateEventListener<SearchKeywordsComponentModel>();
  }

  keywordStringToArray(keywordString?: string): string[] {
    if (!keywordString) {
      return [];
    }

    const separators = [',', '，'];
    if (separators.includes(keywordString[keywordString.length - 1])) {
      keywordString = keywordString.slice(0, -1);
    }

    if (separators.includes(keywordString[0])) {
      keywordString = keywordString.slice(1);
    }
    return _.flatten(
      keywordString.split(',').map((keyword) =>
        keyword
          .trim()
          .split('，')
          .map((keyword) => keyword.trim())
      )
    );
  }

  changeSearchKeyword(searchKeywords?: string) {
    this.searchKeywords = searchKeywords;
    this.updateState(false);
  }

  syncModel(model: SearchKeywordsComponentModel) {
    this.searchKeywords = model.state.searchKeywords;
    this.relatedSearchKeywords = model.state.relatedSearchKeywords;
  }

  onConfirm() {
    const searchKeywords = createSelectOptions(
      _.chain(this.value)
        .map((option) => option.value)
        .concat(this.keywordStringToArray(this.searchKeywords))
        .uniq()
        .value()
    );
    if (searchKeywords.length > 0) {
      this.externalOnChange.call(this, 'searchKeywords', searchKeywords);
    }
  }

  addSearchKeyword(searchKeyword: string) {
    const searchKeywords = createSelectOptions(
      _.chain(this.value)
        .map((option) => option.value)
        .push(searchKeyword)
        .uniq()
        .value()
    );
    if (searchKeywords.length > 0) {
      this.externalOnChange.call(this, 'searchKeywords', searchKeywords);
    }
  }

  get state(): SearchKeywordsComponentState {
    return {
      loading: this.loading,
      searchKeywords: this.searchKeywords,
      relatedSearchKeywords: this.relatedSearchKeywords
    };
  }

  updateState(loading = false) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
